import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import SpeedIcon from "@mui/icons-material/Speed";
import TableChartIcon from "@mui/icons-material/TableChart";

import { withTranslation } from "react-i18next";

import fpDashboardService from "@plannel/services/fp/fp-dashboard-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { FullScreenButton } from "@plannel/components/ActionIconButton";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

// old POInputPlanPerformance.css — red/blue/green-style-color(글자색). 위젯 5/6 css엔 -right
// 변형이 누락돼 실적수량 색이 안 먹던 old 버그가 있어(§0-8) 세 그리드에 동일하게 적용한다.
const CELL_RED = "#FE3333"; // rgb(254,51,51) — 지연/미달
const CELL_BLUE = "#2631FF"; // rgb(38,49,255) — 계획과 동일
const CELL_GREEN = "#008705"; // rgb(0,135,5) — 조기/초과

// 전체화면(카드 1개 확대) 대상 식별자 — §C-4b, 카드 전부에 버튼을 둔다.
const CARD = {
  INPUT_RATE: "inputRateTile",
  OUTPUT_RATE: "outputRateTile",
  TOTAL_OUTPUT: "totalOutputTile",
  INPUT_GRID: "inputPoGrid",
  OUTPUT_GRID: "outputPoGrid",
  ITEM_GRID: "itemPlanGrid",
};

// old getFormattedNumber 대응 — 천단위 + 소수 최대 2자리(§C-4c, 5008과 동일 로컬 헬퍼).
const formatNumber = (value) => {
  if (value == null || value === "") return value;
  const num = Number(value);
  return Number.isNaN(num) ? value : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// old styleCallback 교정판(§0-8) — old는 수량을 new Date()로 감싸 비교해 null 실적이 항상
// 초록으로 칠해졌다(실제로는 미달). 숫자 비교로 바로잡는다: 미달=red / 동일=blue / 초과=green.
const actualQtyCellStyle = (params) => {
  if (!params.data) return null;
  const planQty = Number(params.data.planQty ?? 0);
  const actualQty = Number(params.data.prodtnQty ?? 0);
  if (actualQty < planQty) return { color: CELL_RED };
  if (actualQty === planQty) return { color: CELL_BLUE };
  return { color: CELL_GREEN };
};

// 실적일 색 — old CATEGORY5 styleCallback(정상 동작 쪽) 규칙: 지연=red / 정시=blue / 조기=green.
// 실적이 아직 없으면(old는 "오늘"로 간주 → 과거 계획은 사실상 red) 명시적으로 red 처리한다.
// planDt/prodtnDt는 ISO(yyyy-MM-dd) 문자열이라 사전순 비교가 곧 날짜 비교다.
const actualDateCellStyle = (params) => {
  if (!params.data?.planDt) return null;
  const { planDt, prodtnDt } = params.data;
  if (!prodtnDt || prodtnDt > planDt) return { color: CELL_RED };
  if (prodtnDt === planDt) return { color: CELL_BLUE };
  return { color: CELL_GREEN };
};

const formatMonthDay = (params) => dateUtils.formatDate(params.value, "MM/dd");

// old inputActualGridItems/outputActualGridItems — 두 그리드가 완전 동일 정의(6컬럼 1:1).
// old 폭은 fitStyle:"fill" 비례 가중치(100:30:40:40:40:40)라 그대로 못 쓰고, §C-4c대로
// 내용 기준 고정 폭을 컬럼별로 준다. 날짜 포맷 "MM/dd"도 old 그대로.
const PO_COLUMN_DEFS = [
  { headerName: "FP_PO", field: "prodtnOrderCd", width: 130, type: ["centerAligned"] },
  { headerName: "FP_ITEM", field: "itemNm", width: 150 },
  { headerName: "FP_PLANNED_QTY", field: "planQty", width: 100, type: ["numberColumn", "rightAligned"] },
  { headerName: "FP_PLAN_DATE", field: "planDt", width: 90, type: ["centerAligned"], valueFormatter: formatMonthDay },
  { headerName: "FP_ACTUAL_QTY", field: "prodtnQty", width: 100, type: ["numberColumn", "rightAligned"], cellStyle: actualQtyCellStyle },
  { headerName: "FP_ACTUAL_DATE", field: "prodtnDt", width: 90, type: ["centerAligned"], valueFormatter: formatMonthDay, cellStyle: actualDateCellStyle },
];

// old totalActualGridItems — 4컬럼 1:1. old도 품목코드(CATEGORY1)는 그리드에 노출하지 않았다.
const ITEM_COLUMN_DEFS = [
  { headerName: "FP_ITEM", field: "itemNm", width: 180 },
  { headerName: "FP_PLAN_DATE", field: "planDt", width: 90, type: ["centerAligned"], valueFormatter: formatMonthDay },
  { headerName: "FP_PLANNED_QTY", field: "planQty", width: 110, type: ["numberColumn", "rightAligned"] },
  { headerName: "FP_ACTUAL_QTY", field: "prodtnQty", width: 110, type: ["numberColumn", "rightAligned"], cellStyle: actualQtyCellStyle },
];

const FpVersusPerformance = ({ t, viewName, title }) => {
  // 검색조건은 계획범위(PlanScope) 하나만 — old 충실(2026-08-25 사용자 판정).
  // old ETL은 버전을 WHERE가 아니라 (versionCd, woCd) 조인 키로만 썼고, 오더 적재 정책도
  // old와 같은 "현재 릴리즈 1세트"라 버전 필터가 불필요하다. 이관 초기에 얹었던
  // VersionSearchGroup은 오더가 없는 시뮬레이션 버전이 기본 선택되면 0건이 나는 함정이라
  // 제거했다(서버의 versionCd 가드는 남아 있어, 버전별 적재로 바뀌면 필드만 다시 실으면 된다).
  const [planScope, setPlanScope] = useState("");

  // kpi 응답 1건을 타일 3개가 나눠 쓴다(old는 같은 프로시저 브랜치를 위젯 3개가 3중 호출 — §0-8 교정).
  const [kpi, setKpi] = useState(null);
  const [inputPoRows, setInputPoRows] = useState([]);
  const [outputPoRows, setOutputPoRows] = useState([]);
  const [itemRows, setItemRows] = useState([]);

  // old TotalProdCompletAmount의 AMT/QTY 토글 — 'A'=금액(VALUE4), 'Q'=수량(VALUE3). 초기값 'A'.
  const [amtQtyMode, setAmtQtyMode] = useState("A");

  // 그리드 3개 — ref는 로딩 오버레이용, api state는 컬럼 적용용(재마운트 대응). 5002 선례.
  const inputGridRef = useRef();
  const outputGridRef = useRef();
  const itemGridRef = useRef();
  const [inputGridApi, setInputGridApi] = useState(null);
  const [outputGridApi, setOutputGridApi] = useState(null);
  const [itemGridApi, setItemGridApi] = useState(null);

  const [fullScreen, setFullScreen] = useState(false);
  const [fullScreenCardId, setFullScreenCardId] = useState(null);

  // 전체화면 전환이 그리드를 재마운트해 컬럼 순서/폭/정렬이 초기화되는 것을 막는 3점 세트(§C-4b).
  const inputColumnStateRef = useRef(null);
  const outputColumnStateRef = useRef(null);
  const itemColumnStateRef = useRef(null);
  const captureColumnStates = () => {
    const capture = (gridRef, stateRef) => {
      const api = gridRef.current?.api;
      if (api && !api.isDestroyed()) stateRef.current = api.getColumnState();
    };
    capture(inputGridRef, inputColumnStateRef);
    capture(outputGridRef, outputColumnStateRef);
    capture(itemGridRef, itemColumnStateRef);
  };

  // 병렬 API 4개가 한 조회 단위 — 토큰 1개(§0-8).
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => {
    const settings = FpGridSetting({ title, viewName });
    return {
      ...settings,
      // 읽기 전용 대시보드 — 저장/삭제/DataState 추적 없음.
      defaultColDef: { ...settings.defaultColDef, editable: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setGridsLoading = (loading) => {
    inputGridRef.current?.api?.setGridOption("loading", loading);
    outputGridRef.current?.api?.setGridOption("loading", loading);
    itemGridRef.current?.api?.setGridOption("loading", loading);
  };

  const loadData = (scope) => {
    const requestToken = ++latestReqRef.current;
    // 서버가 읽는 필드만 전송(§A-3) — planScope뿐. versionCd 미전송 = old처럼 버전 무관 집계.
    const params = { planScope: scope };

    setGridsLoading(true);

    fpDashboardService.getFpVersusPerformanceKpi(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setKpi(response?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setKpi(null);
        debug(e);
      });

    fpDashboardService.getFpVersusPerformancePoPlanActual("START", params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setInputPoRows(Array.isArray(response?.data) ? response.data : []);
        inputGridRef.current?.api?.setGridOption("loading", false);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setInputPoRows([]);
        inputGridRef.current?.api?.setGridOption("loading", false);
        debug(e);
      });

    fpDashboardService.getFpVersusPerformancePoPlanActual("END", params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setOutputPoRows(Array.isArray(response?.data) ? response.data : []);
        outputGridRef.current?.api?.setGridOption("loading", false);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setOutputPoRows([]);
        outputGridRef.current?.api?.setGridOption("loading", false);
        debug(e);
      });

    fpDashboardService.getFpVersusPerformanceItemPlanActual(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setItemRows(Array.isArray(response?.data) ? response.data : []);
        itemGridRef.current?.api?.setGridOption("loading", false);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setItemRows([]);
        itemGridRef.current?.api?.setGridOption("loading", false);
        debug(e);
      });
  };

  // 선택형 조회조건 = 즉시 자동조회(§B-1). PlanScope가 최초 로드 시 맨 위 범위를 자동 선택해
  // onChange로 통지하므로(범위가 1개뿐이면 셀렉터는 숨겨지지만 통지는 동일) 최초 조회도
  // 이 effect가 담당한다. 응답 역전은 latestReqRef 토큰이 막는다(§0-8).
  useEffect(() => {
    if (!planScope) return;
    loadData(planScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  // 정적 컬럼이지만 effect로 태운다 — 언어 전환(t) 시 헤더 재번역 + 전체화면 재마운트 시 새 api에
  // 재적용(§C-4b). isDestroyed 가드는 5002 선례(파기된 api에 컬럼 적용 방지). 그리드가 3개라
  // gridId로 p13n 저장 위치를 구분한다(5002 선례).
  useEffect(() => {
    if (!inputGridApi || inputGridApi.isDestroyed()) return;
    GridUtils.setColumnDefs({ api: inputGridApi, columnDefs: PO_COLUMN_DEFS, viewName, gridId: "inputPoGrid", initState: false });
  }, [inputGridApi, viewName, t]);

  useEffect(() => {
    if (!outputGridApi || outputGridApi.isDestroyed()) return;
    GridUtils.setColumnDefs({ api: outputGridApi, columnDefs: PO_COLUMN_DEFS, viewName, gridId: "outputPoGrid", initState: false });
  }, [outputGridApi, viewName, t]);

  useEffect(() => {
    if (!itemGridApi || itemGridApi.isDestroyed()) return;
    GridUtils.setColumnDefs({ api: itemGridApi, columnDefs: ITEM_COLUMN_DEFS, viewName, gridId: "itemPlanGrid", initState: false });
  }, [itemGridApi, viewName, t]);

  // 재마운트 직후 1회만 컬럼 상태 복원 — deps를 [api]로 좁히는 것이 핵심(§C-4b).
  useEffect(() => {
    if (!inputGridApi || inputGridApi.isDestroyed() || !inputColumnStateRef.current) return;
    inputGridApi.applyColumnState({ state: inputColumnStateRef.current, applyOrder: true });
  }, [inputGridApi]);

  useEffect(() => {
    if (!outputGridApi || outputGridApi.isDestroyed() || !outputColumnStateRef.current) return;
    outputGridApi.applyColumnState({ state: outputColumnStateRef.current, applyOrder: true });
  }, [outputGridApi]);

  useEffect(() => {
    if (!itemGridApi || itemGridApi.isDestroyed() || !itemColumnStateRef.current) return;
    itemGridApi.applyColumnState({ state: itemColumnStateRef.current, applyOrder: true });
  }, [itemGridApi]);

  const cardHeader = (Icon, label, cardId) => (
    <Box sx={{ position: "relative", display: "flex", height: "45px", alignItems: "flex-end", justifyContent: "center" }}>
      <Icon fontSize={"medium"} sx={{ mb: 0.2 }} />
      <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ position: "absolute", right: 0, mr: 0.5 }}>
        <FullScreenButton
          fullScreen={fullScreen && fullScreenCardId === cardId}
          setFullScreen={(next) => { captureColumnStates(); setFullScreen(next); }}
          screenId={cardId}
          setScreenId={(next) => { captureColumnStates(); setFullScreenCardId(next); }}
        />
      </Box>
    </Box>
  );

  // 단일 % / 큰 숫자 스탯 타일 — 5009 statTile 패턴(화면 로컬, §C-3b. 공용 추출은 5001 시점
  // 재판단 항목이었고 3종(진행바/스탯/단일%+토글)의 구조가 달라 로컬 유지로 판정).
  const statTile = (cardId, Icon, label, text, sx) => (
    <Card sx={{ flex: 1, height: "100%", ...sx }}>
      {cardHeader(Icon, label, cardId)}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100% - 45px)", px: 2 }}>
        <Typography variant="h4">{text}</Typography>
      </Box>
    </Card>
  );

  const toggleSx = { py: 0, px: 1.2, fontSize: "0.75rem" };

  const renderCard = (cardId, sx) => {
    switch (cardId) {
      // old InputPlanComplianceRate — VALUE1(0~1)×100 을 % 로 표시.
      case CARD.INPUT_RATE:
        return statTile(CARD.INPUT_RATE, SpeedIcon, t("UI_SA_FP_INPUT_RATE"),
          `${formatNumber((kpi?.inputRate ?? 0) * 100)} %`, sx);

      // old ProdCompletComplianceRate — VALUE2(0~1)×100.
      case CARD.OUTPUT_RATE:
        return statTile(CARD.OUTPUT_RATE, SpeedIcon, t("UI_SA_FP_COMPLIANCE_RATE"),
          `${formatNumber((kpi?.outputRate ?? 0) * 100)} %`, sx);

      // old TotalProdCompletAmount — AMT/QTY 토글(초기 AMT). 라벨은 old도 영문 약어 하드코딩이었고
      // 5005의 C/R·D/W/M 토글과 같은 계열(모드 코드)이라 그대로 둔다. 통화 기호는 old에서도
      // 주석 처리돼 미표시("￦ " 표시는 old에 없던 기능이라 이관 대상 아님).
      case CARD.TOTAL_OUTPUT:
        return (
          <Card sx={{ flex: 1, height: "100%", ...sx }}>
            {cardHeader(LeaderboardIcon, t("UI_SA_FP_TOTAL_OUTPUT"), CARD.TOTAL_OUTPUT)}
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", height: 32, px: 1.5 }}>
              <ToggleButtonGroup size="small" exclusive value={amtQtyMode} onChange={(_, value) => { if (value) setAmtQtyMode(value); }}>
                <ToggleButton sx={toggleSx} value="A">AMT</ToggleButton>
                <ToggleButton sx={toggleSx} value="Q">QTY</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100% - 77px)", px: 2 }}>
              <Typography variant="h4">
                {formatNumber(amtQtyMode === "A" ? (kpi?.totalAmt ?? 0) : (kpi?.totalQty ?? 0))}
              </Typography>
            </Box>
          </Card>
        );

      // old POInputPlanPerformance — 최초 투입 공정(START) 미달 상위 20건.
      case CARD.INPUT_GRID:
        return (
          <Card sx={sx}>
            {cardHeader(TableChartIcon, t("UI_SA_FP_INPUT_ACTUAL"), CARD.INPUT_GRID)}
            <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
              <AgGridReact ref={inputGridRef} rowData={inputPoRows} {...defaultGridMemo} onGridReady={(params) => setInputGridApi(params.api)} />
            </Box>
          </Card>
        );

      // old POFinishFpPlanPerformance — 완성품 공정(END) 미달 상위 20건.
      case CARD.OUTPUT_GRID:
        return (
          <Card sx={sx}>
            {cardHeader(TableChartIcon, t("UI_SA_FP_PRODUCT_ACTUAL"), CARD.OUTPUT_GRID)}
            <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
              <AgGridReact ref={outputGridRef} rowData={outputPoRows} {...defaultGridMemo} onGridReady={(params) => setOutputGridApi(params.api)} />
            </Box>
          </Card>
        );

      // old TotalFinishFpPlanPerformance — 품목×계획일 합산 상위 50건.
      case CARD.ITEM_GRID:
        return (
          <Card sx={sx}>
            {cardHeader(TableChartIcon, t("UI_SA_FP_TOTAL_ACTUAL"), CARD.ITEM_GRID)}
            <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
              <AgGridReact ref={itemGridRef} rowData={itemRows} {...defaultGridMemo} onGridReady={(params) => setItemGridApi(params.api)} />
            </Box>
          </Card>
        );

      default:
        return null;
    }
  };

  // old 레이아웃(12컬럼 3×2: 상단 KPI 3장 h4.2 / 하단 그리드 3장 h15 ≈ 22:78)을 % 비율로 근사.
  return (
    <Box className={"grid-area"} sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 560 }}>
      <FilterContainer hasIcons={false}>
        <PlanScope value={planScope} onChange={setPlanScope} />
      </FilterContainer>
      {fullScreen && fullScreenCardId ? (
        // 전체화면 — 검색영역은 그대로 두고 본문만 해당 카드 하나로 교체(§C-4b).
        <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
          {renderCard(fullScreenCardId, { width: "100%", height: "100%" })}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", flex: 1, minHeight: 0 }}>
          <Box sx={{ display: "flex", width: "100%", height: "calc(25% - 6px)", mb: 1.5 }}>
            {renderCard(CARD.INPUT_RATE, { mr: 1.5 })}
            {renderCard(CARD.OUTPUT_RATE, { mr: 1.5 })}
            {renderCard(CARD.TOTAL_OUTPUT)}
          </Box>
          <Box sx={{ display: "flex", width: "100%", height: "calc(75% - 6px)" }}>
            {renderCard(CARD.INPUT_GRID, { width: "calc(33.33% - 8px)", height: "100%", mr: 1.5 })}
            {renderCard(CARD.OUTPUT_GRID, { width: "calc(33.33% - 8px)", height: "100%", mr: 1.5 })}
            {renderCard(CARD.ITEM_GRID, { width: "33.34%", height: "100%" })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default withTranslation()(FpVersusPerformance);
